import { useCallback, useContext, useState } from "react";
import { courseApi } from "configs/api";
import { AppContext } from "context/appContext";
import { SupportAttachmentInput, SupportComment, SupportTicket } from "types/support";
import { mapSupportComment, mapSupportTicket } from "types/supportMappers";

export default function useSupportTickets() {
  const { showAlert } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);

  const listTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await courseApi.get("/api/support/tickets");
      const ok = res.data?.status === true;
      const data = ok && Array.isArray(res.data?.data) ? res.data.data : [];
      setTickets(data.map((t: Record<string, unknown>) => mapSupportTicket(t)));
    } catch {
      setTickets([]);
      showAlert?.("Failed to load tickets", "error");
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  const getTicket = useCallback(
    async (id: string): Promise<SupportTicket | null> => {
      try {
        const res = await courseApi.get(`/api/support/tickets/${id}`);
        if (res.data?.status === true && res.data?.data) {
          return mapSupportTicket(res.data.data as Record<string, unknown>);
        }
        return null;
      } catch {
        showAlert?.("Failed to load ticket", "error");
        return null;
      }
    },
    [showAlert]
  );

  const createTicket = useCallback(
    async (payload: {
      subject: string;
      message: string;
      attachments?: SupportAttachmentInput[];
    }) => {
      const res = await courseApi.post("/api/support/tickets", payload);
      if (res.data?.status === true) {
        showAlert?.("Ticket created successfully", "success");
        return mapSupportTicket(res.data.data as Record<string, unknown>);
      }
      throw new Error(res.data?.message || "Failed to create ticket");
    },
    [showAlert]
  );

  const updateStatus = useCallback(
    async (id: string, status: string) => {
      const res = await courseApi.patch(`/api/support/tickets/${id}/status`, { status });
      if (res.data?.status === true) {
        showAlert?.("Status updated", "success");
        return mapSupportTicket(res.data.data as Record<string, unknown>);
      }
      throw new Error(res.data?.message || "Failed to update status");
    },
    [showAlert]
  );

  const escalateTicket = useCallback(
    async (id: string) => {
      const res = await courseApi.post(`/api/support/tickets/${id}/escalate`);
      if (res.data?.status === true) {
        showAlert?.("Ticket sent to admins", "success");
        return mapSupportTicket(res.data.data as Record<string, unknown>);
      }
      throw new Error(res.data?.message || "Failed to escalate ticket");
    },
    [showAlert]
  );

  const listComments = useCallback(
    async (ticketId: string): Promise<SupportComment[]> => {
      const res = await courseApi.get(`/api/support/tickets/${ticketId}/comments`);
      if (res.data?.status === true && Array.isArray(res.data?.data)) {
        return res.data.data.map((c: Record<string, unknown>) => mapSupportComment(c));
      }
      return [];
    },
    []
  );

  const addComment = useCallback(
    async (
      ticketId: string,
      body: string,
      attachments?: SupportAttachmentInput[]
    ) => {
      const res = await courseApi.post(`/api/support/tickets/${ticketId}/comments`, {
        body,
        attachments,
      });
      if (res.data?.status === true) {
        showAlert?.("Comment added", "success");
        return mapSupportComment(res.data.data as Record<string, unknown>);
      }
      throw new Error(res.data?.message || "Failed to add comment");
    },
    [showAlert]
  );

  const uploadFile = useCallback(async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await courseApi.post("/api/support/upload", formData);
    if (res.data?.status === true && res.data?.data) {
      const d = res.data.data as Record<string, unknown>;
      return {
        fileUrl: String(d.url ?? d.Url ?? ""),
        fileName: (d.fileName ?? d.FileName) as string | undefined,
        contentType: (d.contentType ?? d.ContentType) as string | undefined,
        sizeBytes: Number(d.sizeBytes ?? d.SizeBytes ?? 0),
      } as SupportAttachmentInput;
    }
    throw new Error(res.data?.message || "Upload failed");
  }, []);

  return {
    loading,
    tickets,
    listTickets,
    getTicket,
    createTicket,
    updateStatus,
    escalateTicket,
    listComments,
    addComment,
    uploadFile,
  };
}
